import { useEffect, useState } from 'react';
import { api } from '../../api/client';
import { t } from '../../hooks/useT';

export interface StockPhotoCandidate {
  provider: string;
  id: string;
  photographer: string;
  url?: string;
  previewUrl: string;
  downloadUrl: string;
  width: number;
  height: number;
  sourceNote: string;
}

interface Props {
  puzzleId: string;
  answer: string;
  searchQuery?: string;
  provider: 'auto' | 'pexels' | 'unsplash';
  onClose: () => void;
  onApplied: (puzzle: unknown) => void;
}

export function AdminPhotoPicker({
  puzzleId,
  answer,
  searchQuery,
  provider,
  onClose,
  onApplied,
}: Props) {
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState('');
  const [resolvedQuery, setResolvedQuery] = useState('');
  const [candidates, setCandidates] = useState<StockPhotoCandidate[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const loadCandidates = (excludeIds: string[] = []) => {
    setLoading(true);
    setError('');
    setSelectedId(null);
    api<{ searchQuery: string; candidates: StockPhotoCandidate[] }>(
      `/admin/puzzles/${puzzleId}/photo-candidates`,
      {
        method: 'POST',
        body: JSON.stringify({
          answer: answer.trim(),
          searchQuery: searchQuery?.trim() || undefined,
          provider,
          excludeCandidateIds: excludeIds,
        }),
      },
    )
      .then((res) => {
        setResolvedQuery(res.searchQuery);
        setCandidates(res.candidates);
        if (res.candidates.length > 0) {
          setSelectedId(`${res.candidates[0].provider}:${res.candidates[0].id}`);
        }
      })
      .catch((e: unknown) => {
        const err = e as { data?: { error?: string } };
        setError(
          err.data?.error === 'stock_photo_unavailable'
            ? t('admin.puzzles.stockUnavailable')
            : t('admin.puzzles.photoPickerLoadError'),
        );
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadCandidates();
  }, [puzzleId, answer, searchQuery, provider]);

  const selected = candidates.find((c) => `${c.provider}:${c.id}` === selectedId) ?? null;

  const handleApply = async () => {
    if (!selected) return;
    setApplying(true);
    setError('');
    try {
      const res = await api<{ puzzle: unknown }>(`/admin/puzzles/${puzzleId}/refetch-photo`, {
        method: 'POST',
        body: JSON.stringify({
          answer: answer.trim(),
          searchQuery: searchQuery?.trim() || undefined,
          provider,
          candidate: selected,
        }),
      });
      onApplied(res.puzzle);
      onClose();
    } catch (e: unknown) {
      const err = e as { data?: { error?: string } };
      setError(
        err.data?.error === 'stock_photo_unavailable'
          ? t('admin.puzzles.stockUnavailable')
          : t('admin.puzzles.imageError'),
      );
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="admin-crop-overlay" role="dialog" aria-modal="true">
      <div className="admin-photo-picker-modal">
        <header className="admin-crop-head">
          <h2>{t('admin.puzzles.photoPickerTitle')}</h2>
          <button type="button" className="admin-crop-close" onClick={onClose} aria-label={t('admin.crops.cancel')}>
            ×
          </button>
        </header>

        <p className="admin-crop-hint">
          {t('admin.puzzles.photoPickerHint')}
          {resolvedQuery && (
            <>
              {' '}
              <strong>{resolvedQuery}</strong>
            </>
          )}
        </p>

        {loading && <p className="admin-msg">{t('admin.puzzles.photoPickerLoading')}</p>}

        {!loading && candidates.length > 0 && (
          <div className="admin-photo-picker-grid">
            {candidates.map((c) => {
              const key = `${c.provider}:${c.id}`;
              const isSelected = selectedId === key;
              return (
                <button
                  key={key}
                  type="button"
                  className={`admin-photo-picker-item${isSelected ? ' is-selected' : ''}`}
                  onClick={() => setSelectedId(key)}
                  aria-pressed={isSelected}
                >
                  <img src={c.previewUrl} alt="" loading="lazy" draggable={false} />
                  <span className="admin-photo-picker-meta">
                    <span className="admin-badge admin-badge-provider">{c.provider}</span>
                    <span className="admin-photo-picker-photographer">{c.photographer}</span>
                    <span className="admin-photo-picker-dims">{c.width}×{c.height}</span>
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {!loading && candidates.length === 0 && !error && (
          <p className="admin-msg">{t('admin.puzzles.stockUnavailable')}</p>
        )}

        {error && <p className="admin-msg admin-msg-error">{error}</p>}

        <div className="admin-photo-picker-actions">
          <button
            type="button"
            className="btn secondary admin-btn-sm"
            disabled={loading || applying}
            onClick={() => loadCandidates(candidates.map((c) => c.id))}
          >
            {loading ? t('common.loading') : t('admin.puzzles.photoPickerRefresh')}
          </button>
          <button
            type="button"
            className="btn primary"
            disabled={!selected || applying || loading}
            onClick={() => void handleApply()}
          >
            {applying ? t('common.loading') : t('admin.puzzles.photoPickerApply')}
          </button>
          <button type="button" className="btn secondary" onClick={onClose}>
            {t('admin.crops.cancel')}
          </button>
        </div>
      </div>
    </div>
  );
}
